import { ChangeDetectionStrategy, AfterViewInit, Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { SecurityService } from '@wsphere/staff/web';
import { Claim } from '@wsphere/warranties/domain';
import { catchError, combineLatest, from, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { FeatureFlagsService } from '../../../policy/services/feature-flags.service';
import { ClaimIntakeCardComponent } from '../../components/claim-intake-card/claim-intake-card.component';
import { ClaimItemDetailsDialog, OpenClaimDialog } from '../../dialogs';
import { ClaimResolutionOptionsService, ClaimService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-overview',
  templateUrl: './claim-overview.page.html',
  standalone: false,
})
export class ClaimOverviewPage implements AfterViewInit {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private claimSvc: ClaimService,
    private securitySvc: SecurityService,
    private toastSvc: ToastService,
    private claimResolutionOptionsSvc: ClaimResolutionOptionsService,
    private featureFlagsSvc: FeatureFlagsService
  ) {
    this.route.parent?.queryParams.subscribe(async params => {
      this.openCreateDialog = params?.['createItem'] === 'true';
      this.closeItemDialog = params?.['closeItemDialog'] === 'true';
      if (this.closeItemDialog) {
        await this.refresh();
        this.router.navigate(['.'], { relativeTo: this.route, queryParams: null });
      }
    });
  }

  @ViewChild('claimDialog') claimDialog?: OpenClaimDialog;
  @ViewChild('createItemDialog') createItemDialog!: ClaimItemDetailsDialog;
  @ViewChild(ClaimIntakeCardComponent) intakeCard?: ClaimIntakeCardComponent;

  routeRefresh$ = this.route.queryParams.pipe(
    map(async params => {
      const refresh = params['refresh'] === 'true';
      if (refresh) {
        await this.refresh();
        this.router.navigate(['.'], { relativeTo: this.route, queryParams: null });
      }
    })
  );

  readonly unspecifiedResolutionValue = Claim.CLAIM_RESOLUTION_UNSPECIFIED_FILTER_VALUE;
  protected selectedResolution = this.unspecifiedResolutionValue;
  protected resolutionSaving = false;

  readonly claim$ = this.claimSvc.getClaim().pipe(
    tap(claim => {
      this.selectedResolution = claim?.resolution ?? this.unspecifiedResolutionValue;
    }),
    shareReplay(1)
  );

  protected resolutionOptions$ = combineLatest([
    this.claim$,
    from(this.claimResolutionOptionsSvc.listOptions()).pipe(catchError(() => of<string[]>([]))),
  ]).pipe(
    map(([claim, options]) => {
      if (claim?.resolution && !options.includes(claim.resolution)) {
        return [claim.resolution, ...options];
      }

      return options;
    }),
    shareReplay(1)
  );

  canViewClaim$ = this.claim$.pipe(
    switchMap(async claim => {
      const permissionKey = claim?.warranty?.distributor?.permissionKey
        ? [claim?.warranty?.distributor?.permissionKey]
        : null;
      return await this.securitySvc.hasAccess('clm_ViewClaim', permissionKey);
    })
  );

  canEditClaim$ = this.claim$.pipe(
    switchMap(async claim => {
      const permissionKey = claim?.warranty?.distributor?.permissionKey
        ? [claim?.warranty?.distributor?.permissionKey]
        : null;

      return await this.securitySvc.hasAccess('clm_EditClaim', permissionKey);
    })
  );

  canAuthorizeClaim$ = this.claim$.pipe(
    switchMap(async claim => {
      const permissionKey = claim?.warranty?.distributor?.permissionKey
        ? [claim?.warranty?.distributor?.permissionKey]
        : null;
      return await this.securitySvc.hasAccess('clm_AuthorizeClaim', permissionKey);
    })
  );

  canReverseAuthorization$ = this.claim$.pipe(
    switchMap(async claim => {
      const permissionKey = claim?.warranty?.distributor?.permissionKey
        ? [claim?.warranty?.distributor?.permissionKey]
        : null;
      return await this.securitySvc.hasAccess('clm_ReverseClaimAuthorization', permissionKey);
    })
  );

  isZeroPayAllowed$ = this.featureFlagsSvc.isEnabled('CLAIM_ZERO_PAY');

  canOpenClaim$ = this.claim$.pipe(
    switchMap(async claim => {
      const permissionKey = claim?.warranty?.distributor?.permissionKey
        ? [claim?.warranty?.distributor?.permissionKey]
        : null;
      return await this.securitySvc.hasAccess('clm_OpenClaim', permissionKey);
    })
  );

  open = Claim.Status.OPEN;
  usesNewFlow = Claim.Authorization.usesNewFlow;
  claimId: string | null = null;
  openCreateDialog: boolean | null = null;
  closeItemDialog: boolean | null = null;

  protected hasResolutionChanged(claim: Claim.Model) {
    return (claim.resolution ?? this.unspecifiedResolutionValue) !== this.selectedResolution;
  }

  protected async saveResolution(claim: Claim.Model) {
    this.resolutionSaving = true;

    const resolution =
      this.selectedResolution === this.unspecifiedResolutionValue
        ? null
        : Claim.normalizeClaimResolutionValue(this.selectedResolution);

    const response = await this.claimSvc.updateResolution(claim.id, { resolution }).catch(({ error }) => {
      this.toastSvc.show({
        type: 'error',
        title: 'Something went wrong',
        text: error.message,
      });
      return null;
    });

    if (response) {
      this.toastSvc.show({
        type: 'success',
        title: 'Claim Resolution Updated',
        text: `<strong>Claim #${claim.claimNumber}</strong> has been successfully updated.`,
      });
      await this.refresh();
    }

    this.resolutionSaving = false;
  }

  openClaimDialog() {
    this.claimDialog?.openDialog();
  }

  openCreateItemDialog() {
    this.createItemDialog?.openDialog();
  }

  /** The item's page holds its charges, cure and adjudication. */
  openItem(itemId: string) {
    this.router.navigate([itemId], { relativeTo: this.route });
  }

  /** Jumps to whatever the authorization checklist still needs: the job, or a new one when there's none. */
  fixItem(itemId: string | null) {
    if (itemId) this.openItem(itemId);
    else this.openCreateItemDialog();
  }

  async refresh() {
    await this.claimSvc.refreshClaim();
  }

  ngAfterViewInit(): void {
    if (this.openCreateDialog) {
      this.openCreateItemDialog();
      this.router.navigate(['.'], { relativeTo: this.route });
    }
  }
}
